/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useEffect, useRef } from 'react';
import {
  Breadcrumb,
  Layout,
  Spin,
} from 'antd';
import { useHistory } from 'react-router-dom';
import { FormikProvider } from 'formik';
import breadcrumbSeparator from '../../../assets/icons/breadcrumbSeparator.svg';
import BreadcrumbItem from '../../lib/breadcrumbItem';
import MapReportData from './map-reportData';
import { useActions } from '../../providers/actions-provider/actions.context';
import { useStore } from '../../../store';
import { AssetSearch } from './components/AssetSearch';
import { RefreshButton } from './components/RefreshButton';
import { FilterButton } from './components/FilterButton';
import { FilterDrawer } from './components/FilterDrawer';
import { ConfigurationButton } from './components/ConfigurationButton';
import { MapHeader } from './components/MapHeader';
import { ConfigurationDrawer } from './components/ConfigurationDrawer';
import { MapComponent } from './map/MapComponent';
import { LazyList } from './components/LazyList';
import { useMapContextHook } from './context/use-map-context-hook';
import { MapContext } from './context/map-context';
import { SortComponent } from './components/SortComponent';
import { useFilterSortContextHook } from './context/use-filter-sort-context';
import { FilterSortContext } from './context/filter-sort-context';
import { AssetDetails } from './components/AssetDetails';
import { ReactComponent as ArrowRight } from '../../../assets/icons/arrowRight.svg';
import { ReactComponent as ArrowLeft } from '../../../assets/icons/arrowLeft.svg';
import { ReportTableButton } from './components/ReportTableButton';
import checkPermission from '../../../utils/permissions';
import { useLocalStorage } from '../../providers/local-storage-provider/local-storage.context';
import { ISavedReportFilter } from './types';
import { mergeFilters, mergeSorts } from '../edit-report/reportParamsHelpers';
import isNumber from '../../../utils/is-number';
import useQuery from '../../../utils/useQuery';

const MapReport = (): JSX.Element => {
  const mapContext = useMapContextHook();
  const filterSortContext = useFilterSortContextHook();
  const { reportActions, facilityActions } = useActions();
  const localStorageService = useLocalStorage();

  const {
    executedReport,
    listsForReport,
    mapReport,
    groups,
    mapReportDefinition,
    zonesForMaps,
    mapOrigin,
    mapImageImage,
  } = useStore();

  const queryParams = useQuery();
  const keys = queryParams.keys();
  let result = keys.next();
  // eslint-disable-next-line
  const paramsMap: any = {};
  while (!result.done) {
    paramsMap[result.value] = { $eq: queryParams.get(result.value) };
    result = keys.next();
  }

  const filtersFromQueryParams = Object.keys(paramsMap).length > 0 ? JSON.stringify(paramsMap) : '';

  const { location: { pathname } } = useHistory();
  const positionOfExec = pathname.indexOf('exec-');
  const positionOfID = pathname.indexOf('/', positionOfExec + 5);
  const id = pathname
    .slice(positionOfExec + 5, positionOfID === -1 ? pathname.length : positionOfID);
  const sortRef = useRef<HTMLDivElement | null>(null);

  const hasNotWritePermission = !checkPermission('reports', 'write');
  const userSetFilters = hasNotWritePermission ? localStorageService.get<ISavedReportFilter>('REPORT_FILTERS') : null;
  const matchingReport = userSetFilters?.reportId === id;

  const userFilters = matchingReport ? userSetFilters?.filters : null;
  const userSorts = matchingReport ? userSetFilters?.sorts : null;

  let activeFilters = filterSortContext.findFilter;
  let activeSorts = filterSortContext.sort;

  if (hasNotWritePermission) {
    activeFilters = mergeFilters({
      serverFiltersString: filterSortContext.findFilter,
      userFiltersString: userFilters as string,
    });
    activeSorts = mergeSorts({
      serverSortsString: filterSortContext.sort,
      userSortsString: userSorts as string,
    });
  }

  const appliedFilters = filterSortContext.submit ? (activeFilters || filtersFromQueryParams)
    : activeFilters;

  const {
    title,
    routes,
  } = MapReportData(appliedFilters, activeSorts,
    mapContext.skip, mapContext.configurationValues.pageSize,
    mapContext.setHasAssetFilter);

  const loading = executedReport.loading
  || listsForReport.loading
  || mapReport.loading
  || groups.loading
  || mapReportDefinition.loading
  || zonesForMaps.loading
  || mapOrigin.loading
  || mapImageImage.loading;

  const reportCode = pathname.split('/')[4] || '';
  const { find, sort } = executedReport.data;

  const refreshHandler = () => {
    const getReportArg = isNumber(reportCode) ? reportCode : executedReport.data.id;
    reportActions.mapReportDefinitionByReport(getReportArg as number);
    facilityActions.getGroups();
    const isIdNumber = isNumber(id);
    if (isIdNumber) {
      reportActions.getExecutedReport(+id);
      reportActions.getListsForReport(id);
      reportActions.getMapReport(reportCode, filterSortContext.findFilter,
        sort, mapContext.skip, mapContext.configurationValues.pageSize);
    } else {
      reportActions.queryReportAndExecute(id, appliedFilters, activeSorts);
      reportActions.getListsForReport((executedReport.data.id)?.toString());
      reportActions.getMapReport(reportCode, appliedFilters,
        activeSorts, mapContext.skip, mapContext.configurationValues.pageSize);
    }
  };

  useEffect(() => {
    if (!groups.loaded) {
      facilityActions.getGroups();
    }
  }, []);

  useEffect(() => {
    const isIdNumber = isNumber(id);
    if (isIdNumber) {
      reportActions.getExecutedReport(+id);
      reportActions.getListsForReport(id);
    } else {
      reportActions.queryReportAndExecute(id, appliedFilters, activeSorts);
      reportActions.getListsForReport((executedReport.data.id)?.toString());
    }
  }, [id, executedReport.data.id, appliedFilters, activeSorts]);

  return (
    <MapContext.Provider value={mapContext}>
      <FilterSortContext.Provider value={filterSortContext}>
        <FormikProvider value={filterSortContext.formik}>
          <Layout style={{ overflow: 'hidden', height: '100%' }}>
            <Spin
              spinning={loading || (
                (mapReport?.data?.code)?.toUpperCase() !== reportCode.toUpperCase()
              )}
              size="large"
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 1000,
              }}
            >
              <div style={{
                display: 'flex', height: '100%', width: '100%',
              }}
              >
                <div style={{ flexGrow: 1, transition: 'width 0.3s ease' }}>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    paddingTop: '24px',
                    paddingLeft: '24px',
                    paddingRight: '24px',
                    paddingBottom: '24px',
                    borderBottom: '1px solid #81848C',
                  }}
                  >
                    <div style={{ height: '12px', display: 'flex' }}>
                      <Breadcrumb
                        itemRender={BreadcrumbItem}
                        routes={routes}
                        separator={<img src={breadcrumbSeparator} alt="breadcrumb separator" />}
                        params={{
                          routes,
                        }}
                      />
                      <p style={{ paddingLeft: '10px', color: '#3800ee' }}>{mapOrigin.data.name}</p>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <MapHeader title={title} />
                      <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                        <RefreshButton click={() => refreshHandler()} />
                        <FilterButton />
                        <FilterDrawer />
                        <ReportTableButton />
                        <ConfigurationButton />
                      </div>
                    </div>
                  </div>

                  {!loading && <MapComponent />}
                </div>
                <div
                  style={{
                    cursor: 'pointer',
                    position: 'absolute',
                    right: '5px',
                    top: '5px',
                  }}
                  onClick={() => mapContext.onCollapseAssetBar()}
                >
                  {!mapContext.collapseAssetBar ? <ArrowRight /> : <ArrowLeft />}
                </div>
                <div style={{
                  width: mapContext.collapseAssetBar ? '0px' : '365px', borderLeft: '1px solid #E1E3E8', height: '100%', transition: 'width 0.3s ease',
                }}
                >
                  {!mapContext.collapseAssetBar && mapContext.openConfigurationDrawer && (
                    <ConfigurationDrawer />
                  )}
                  {!mapContext.collapseAssetBar && !mapContext.openConfigurationDrawer
                  && !mapContext.showAssetDetails
                    ? (
                      <>
                        <AssetSearch />
                        <div ref={sortRef}>
                          {filterSortContext.sortOpened && <SortComponent />}
                        </div>
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          height: !filterSortContext.sortOpened
                            ? 'calc(100vh - 140px)'
                            : `calc(100vh - ${140 + (sortRef?.current?.offsetHeight || 0)}px)`,
                          // overflow: 'hidden',
                          backgroundColor: '#fff',
                        }}
                        >
                          {Object.values(mapReport.data.recordsMap)
                          && !mapContext.groupSelected.selectedGroup.length
                           && (
                             <LazyList
                               items={Object.values(mapReport.data.recordsMap)}
                               loading={loading}
                             />
                           )}
                          {(mapContext.groupSelected.selectedGroup.length
                            && Object.values(mapReport.data.recordsMap)) ? (
                              <LazyList
                                items={mapContext.groupSelected.selectedGroup}
                                loading={loading}
                                type="selected"
                              />

                            ) : null}
                          {(mapContext.groupSelected.notSelectedGroup.length
                            && Object.values(mapReport.data.recordsMap)) ? (
                              !filterSortContext.sortOpened && (
                                <LazyList
                                  items={mapContext.groupSelected.notSelectedGroup}
                                  loading={loading}
                                  type="notSelected"
                                />
                              )
                            ) : null}
                        </div>
                      </>
                    )
                    : (
                      <AssetDetails />
                    )}
                </div>

              </div>
            </Spin>
          </Layout>
        </FormikProvider>
      </FilterSortContext.Provider>
    </MapContext.Provider>
  );
};
MapReport.displayName = 'MapReport';
export default MapReport;
